import { combineEpics, createEpicMiddleware as createMiddleware, Epic } from 'redux-observable';
import { shareReplay, Subject, switchMap } from 'rxjs';
import { NotificationOptions } from 'components/elements';
import {
  createCreateAccount$,
  createGetAccount$,
  createGetAccountList$,
  createGetAccountSummary$,
  createUpdateAccount$,
} from 'services/accounts';
import { createToken$, createTokenRequestStatus$, getSigninToken$ } from 'services/auth';
import { createConfig$ } from 'services/config';
import { createCreateNote$, createGetNote$, createGetNoteList$ } from 'services/notes';
import {
  createArchiveTransactionList$,
  createGetBankTransactionList$,
  createGetSystemTransactionList$,
  createMatchTransactionList$,
  createRestoreTransactionList$,
  createUnmatchTransactionList$,
  createAutoMatchTransactionList$
} from 'services/transactions';
import accountsEpic$ from './accounts/epics';
import { RootAction } from './actions';
import authEpic$ from './auth/epics';
import notesEpic$ from './notes/epics';
import notificationsEpic$ from './notifications/epics';
import { State } from './reducers';
import systemEpic$ from './system/epics';
import transactionsEpic$ from './transactions/epics';

/**
 * Create React Observable epic dependencies
 *
 * @param {Subject<NotificationOptions>} notification$ System notification observable
 */
const createEpicDependencies = (notification$: Subject<NotificationOptions>) => {
  // Create an observable emitting the current application config
  const config$ = createConfig$().pipe(shareReplay(1));

  // Create observable emitting latest authorization tokens (ensuring configuration is loaded first)
  const requesting$ = createTokenRequestStatus$();
  const token$ = config$.pipe(switchMap(() => createToken$(requesting$)));

  return {
    archiveTransaction$: createArchiveTransactionList$(token$),
    config$,
    createAccount$: createCreateAccount$(token$),
    createNote$: createCreateNote$(token$),
    getAccount$: createGetAccount$(token$),
    getAccountList$: createGetAccountList$(token$),
    getAccountSummary$: createGetAccountSummary$(token$),
    getBankTransactionList$: createGetBankTransactionList$(token$),
    getNote$: createGetNote$(token$),
    getNoteList$: createGetNoteList$(token$),
    getSigninToken$,
    getSystemTransactionList$: createGetSystemTransactionList$(token$),
    matchTransaction$: createMatchTransactionList$(token$),
    notification$,
    restoreTransaction$: createRestoreTransactionList$(token$),
    unmatchTransaction$: createUnmatchTransactionList$(token$),
    updateAccount$: createUpdateAccount$(token$),
    autoMatchTransaction$: createAutoMatchTransactionList$(token$),
  };
};

/**
 * Create root observable epic
 */
export const createRootEpic = (): Epic<RootAction, RootAction, State, EpicDependencies> =>
  combineEpics<RootAction, RootAction, State, EpicDependencies>(
    accountsEpic$,
    authEpic$,
    notesEpic$,
    notificationsEpic$,
    systemEpic$,
    transactionsEpic$,
  );

/**
 * Create Redux Observable epic middleware
 *
 * @param {Subject<NotificationOptions>} notification$ System notification observable
 */
export const createEpicMiddleware = (notification$: Subject<NotificationOptions>) =>
  createMiddleware<RootAction, RootAction, State, EpicDependencies>({
    dependencies: createEpicDependencies(notification$),
  });

export type EpicDependencies = ReturnType<typeof createEpicDependencies>;
